import { Dispatch, memo, ReactNode, SetStateAction, useState } from "react";
import {
	Control,
	Controller, ControllerRenderProps,
	FieldErrors,
	useFieldArray,
	UseFormSetValue,
	useFormState,
} from "react-hook-form";
import { intl } from "LocaleWrapper";
import moment from "moment";

import { Button, Card, Checkbox, FormGroup, Input, Select } from "@cofano/cck";
import { Tooltip } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { PercentRounded } from "@mui/icons-material";

import { FormArrayContainer, FormArrayRow } from "components/cck/FormArray";
import FormField from "components/cck/FormField";
import DatePicker from "components/cck/DatePicker";
import { useConfirm } from "components/hooks/useConfirm";
import NoticePeriodSelect from "components/selects/NoticePeriodSelect";
import DateRangePicker from "components/cck/DateRangePicker";

import { FormattedDateWindow } from "utils/DateUtils";
import { Language } from "utils";

import {
	calculateIndexedPricePerUnit,
	createIndexedPriceMap,
	getPricePerUnit,
	getPriceUnitAndPricePerUnitFromRentAgreement,
	sortOptions,
	timeWindowOverlaps,
	timeWindowsOverlap,
} from "view/contractsNew/utils/ContractUtils";

import { IndexationType, TimeWindowInput, TimeUnit, AgreementTypeEnum } from "__generated__/graphql";
import { CurrencyFragment } from "__generated__/graphql/fragments.generated";

import { ContractFormInput, ContractFormOutput } from "view/contractsNew/validation/contract.zod";
import { ContractOptionFormInput } from "view/contractsNew/validation/contractoption.zod";

type Agreement = {
	type: AgreementTypeEnum | null;
	calculationMethod: {
		price?: number | null;
		timeUnit?: TimeUnit | null;
	} | null;
}

type ContractOptionsFormType = {
	control: Control<ContractFormInput, unknown, ContractFormOutput>;
	contractValidWindow?: [ Date | null, Date | null ] | null;
	contractOptions?: ContractOptionFormInput[];
	agreements: Agreement[];
	takenOptionContract: { id: number } | null;
	currency: CurrencyFragment;
	setValue: UseFormSetValue<ContractFormInput>;
}

type ContractOptionFormType = {
	indexedPrices: Map<number, number>;
	setIndexedPrices: Dispatch<SetStateAction<Map<number, number>>>;
	control: Control<ContractFormInput, unknown, ContractFormOutput>;
	errors: FieldErrors<ContractFormInput>;
	contractOptions?: ContractOptionFormInput[];
	contractValidWindow?: [ Date | null, Date | null ] | null;
	currency: CurrencyFragment;
	index: number;
	orderedIndex: number;
	sortedOptions: ContractOptionFormInput[];
	previousOptionIndex: number | null; // null when it is the first option, then there is no previous option
	remove: (index: number) => void;
	setValue: UseFormSetValue<ContractFormInput>;
	calculateRealIndex: (currentOption?: ContractOptionFormInput | number) => number
	hasTakenOptionContract: boolean;
	agreementPricePerUnit: number | null;
	priceUnit: TimeUnit | null;
}

type IndexedPricePerUnitPreviewProps = {
	indexedPrices: Map<number, number>;
	setIndexedPrices: Dispatch<SetStateAction<Map<number, number>>>;
	currentOption: ContractOptionFormInput | null;
	index: number;
	setPricePerUnitForNextOption: (value: number | null) => void;
	currencySymbol?: ReactNode;
	priceUnit: TimeUnit | null;
}

type UseEndPriceOfTheContractFieldProps = {
	field: ControllerRenderProps<ContractFormInput, `contractOptions.${ number }.useEndPriceOfTheContract`>;
	index: number;
	currentOption: ContractOptionFormInput | null;
	previousOptionIndex: number | null;
	indexedPrices: Map<number, number>;
	setValue: UseFormSetValue<ContractFormInput>;
	hasTakenOptionContract: boolean;
	agreementPricePerUnit: number | null;
}

const optionTakenValues = {
	YES: true,
	NO: false,
	OPEN: null,
};

const ContractOptionsForm = memo(({
	control,
	contractValidWindow,
	contractOptions,
	agreements,
	takenOptionContract,
	currency,
	setValue,
}: ContractOptionsFormType) => {

	const { errors } = useFormState({ control, name: "contractOptions" });

	const confirm = useConfirm();

	const { fields, append, remove } = useFieldArray({ control, name: "contractOptions" });

	const [ indexedPrices, setIndexedPrices ] = useState<Map<number, number>>(() => createIndexedPriceMap(contractOptions));

	const onRemove = (index: number) => {
		confirm({
			type: "DELETE",
		}).then(() => remove(index));
	};

	const sortedOptions: ContractOptionFormInput[] = sortOptions(contractOptions);

	const calculateOrderedIndex = (currentOption?: ContractOptionFormInput): number =>
		sortedOptions
			.findIndex(option =>
				typeof currentOption?.validityWindow?.from === "number"
				&& option.validityWindow?.from === currentOption.validityWindow.from);

	const calculateRealIndex = (currentOption?: ContractOptionFormInput | number): number => {
		if (typeof currentOption === "number") {
			currentOption = sortedOptions[currentOption];
		}
		if (typeof currentOption?.validityWindow?.from !== "number") {
			return -1;
		}
		return contractOptions?.findIndex(option =>
			option.validityWindow?.from === currentOption.validityWindow?.from) ?? -1;
	};

	const hasTakenOptionContract: boolean = !!takenOptionContract;

	const [ agreementPricePerUnit, priceUnit ] = getPriceUnitAndPricePerUnitFromRentAgreement(agreements);

	return (
		<div className="contract-options-form">
			<Card
				actions={[ {
					render: () => <Button
						onClick={() => append({
							id: null,
							validityWindow: null,
							description: "",
							indexationType: IndexationType.FixedAmount,
							indexationDate: null,
							indexationAmount: null,
							pricePerUnit: null,
							optionTaken: null,
							useEndPriceOfTheContract: false,
						})}
						disabled={hasTakenOptionContract}
						style={{ marginLeft: 4 }}
						variant="outlined"
						size="small">
						{intl.formatMessage({ id: "ADD_BUTTON" })}
					</Button>,
				} ]}
				disablePadding
			>
				<div className="contract-options">
					<FormArrayContainer>
						{fields.map((option: ContractOptionFormInput, index) => {

							const orderedIndex = calculateOrderedIndex(contractOptions?.[index]);

							return (
								<ContractOptionForm
									key={option.id}
									indexedPrices={indexedPrices}
									setIndexedPrices={setIndexedPrices}
									control={control}
									errors={errors}
									contractOptions={contractOptions}
									contractValidWindow={contractValidWindow}
									currency={currency}
									index={index}
									orderedIndex={orderedIndex}
									sortedOptions={sortedOptions}
									previousOptionIndex={orderedIndex === 0 ? null : calculateRealIndex(orderedIndex - 1)}
									remove={onRemove}
									setValue={setValue}
									calculateRealIndex={calculateRealIndex}
									hasTakenOptionContract={hasTakenOptionContract}
									agreementPricePerUnit={agreementPricePerUnit}
									priceUnit={priceUnit} />
							);
						})}
					</FormArrayContainer>
				</div>
			</Card>
		</div>
	);
});

const ContractOptionForm = ({
	indexedPrices,
	setIndexedPrices,
	control,
	errors,
	contractOptions,
	contractValidWindow,
	currency,
	index,
	orderedIndex,
	sortedOptions,
	previousOptionIndex,
	remove,
	setValue,
	calculateRealIndex,
	hasTakenOptionContract,
	agreementPricePerUnit,
	priceUnit,
}: ContractOptionFormType) => {

	const currentOption: ContractOptionFormInput | null = contractOptions?.[index] ?? null;

	const firstOptionTakenDefined = typeof (sortedOptions[0])?.optionTaken === "boolean";
	const optionTaken =
		orderedIndex > 0 ? firstOptionTakenDefined ? false : null : (currentOption?.optionTaken ?? null);

	const [ prevPricePerUnitFromRentAgreement, setPrevPricePerUnitFromRentAgreement ] = useState<number | null | undefined>();
	if (agreementPricePerUnit !== prevPricePerUnitFromRentAgreement) {
		if (orderedIndex === 0 && currentOption?.useEndPriceOfTheContract) {
			setValue(`contractOptions.${index}.pricePerUnit`, agreementPricePerUnit);
		}
		setPrevPricePerUnitFromRentAgreement(agreementPricePerUnit);
	}

	return (
		<FormArrayRow
			title={
				<span>
					{orderedIndex + 1 + ". "}
					<FormattedDateWindow
						from={currentOption?.validityWindow?.from}
						to={currentOption?.validityWindow?.to}
						format="ll"
					/>
				</span>
			}
			color={ typeof optionTaken === "boolean" ? optionTaken ? "#F39200" : "gray" : "gray" }
			onDelete={() => remove(index)}
			canDelete={!hasTakenOptionContract}
			slotProps={{
				root: {
					sx: {
						marginBottom: 3,
					},
				},
			}}
		>
			<Grid container spacing={3}>
				<Grid size={{ xs: 6 }}>
					<FormGroup label={Language("VALID_BETWEEN")} overflow="hidden" required>
						<FormField errors={errors} name={`contractOptions.${index}.validityWindow`} >
							<Controller
								name={`contractOptions.${index}.validityWindow`}
								control={control}
								rules={{
									required: true,
									validate: (value: TimeWindowInput | null | undefined, formValues) => {
										if (!value?.from || !value.to) {
											return intl.formatMessage({ id: "FROM_AND_TO_REQUIRED" });
										}
										if (timeWindowsOverlap(
											value, {
												from: formValues.validWindow?.[0]?.valueOf(),
												to: formValues.validWindow?.[1]?.valueOf(),
											})) {
											return intl.formatMessage({ id: "OPTION_AND_CONTRACT_VALIDITY_OVERLAP" });
										}
										if (timeWindowOverlaps(value,
											formValues.contractOptions
												?.map(option => option.validityWindow ?? { from: undefined, to: undefined }) ?? [], 1)) {
											return intl.formatMessage({ id: "OPTIONS_VALIDITY_OVERLAPS" });
										}
									},
								}}
								render={({ field }) =>
									<DateRangePicker
										startDate={field.value?.from ? new Date(field.value.from) : undefined}
										endDate={field.value?.to ? new Date(field.value.to) : undefined}
										onChange={window => {
											field.onChange(
												{
													from: window[0]?.setHours(0, 0, 0, 0),
													to: window[1]?.setHours(23, 59, 59, 0),
												},
											);
											const sortedOptions = sortOptions(contractOptions);
											sortedOptions.forEach((option, index) => {
												if (option.useEndPriceOfTheContract) {
													const pricePerUnit = index === 0
														? agreementPricePerUnit
														: calculateIndexedPricePerUnit(sortedOptions[index - 1]);

													setValue(`contractOptions.${calculateRealIndex(option)}.pricePerUnit`, pricePerUnit);
												}
											});
										}}
										minDate={moment(contractValidWindow?.[1] ?? null).add(1, "day").toDate()}
										disabled={field.disabled || hasTakenOptionContract}
										showYearDropdown
										excludeDateIntervals={contractOptions
											?.filter(option => option !== currentOption)
											.flatMap(option => {
												const start =
													option.validityWindow?.from ? new Date(option.validityWindow.from) : undefined;
												const end =
													option.validityWindow?.to ? new Date(option.validityWindow.to) : undefined;
												if (!start || !end) {
													return [];
												}
												return [ { start: start, end: end } ];
											})}
									/>}
							/>
						</FormField>
					</FormGroup>

					<FormGroup label={Language("DESCRIPTION")} overflow="hidden" required>
						<FormField errors={errors} name={`contractOptions.${index}.description`} maxLength={50}>
							<Controller
								name={`contractOptions.${index}.description`}
								control={control}
								rules={{ maxLength: 50, required: true }}
								render={({ field }) => <Input
									{...field}
									value={field.value || undefined}
									disabled={field.disabled || hasTakenOptionContract}
								/>}
							/>
						</FormField>
					</FormGroup>

					<FormGroup label={Language("OPTION_TAKEN")} overflow="hidden">
						<FormField errors={errors} name={`contractOptions.${index}.optionTaken`}>
							<Controller
								name={`contractOptions.${index}.optionTaken`}
								control={control}
								render={({ field }) => <Select
									{...field}
									disabled
									fetchOptions={Object.keys(optionTakenValues)}
									value={Object.keys(optionTakenValues).find(key =>
										optionTaken === optionTakenValues[key as keyof typeof optionTakenValues])}
									getOptionLabel={option => intl.formatMessage({ id: option })}
									onChange={value => field.onChange(optionTakenValues[value as keyof typeof optionTakenValues])}
									disableSearch
									clearable={false}
								/>}
							/>
						</FormField>
					</FormGroup>

					<FormGroup label={Language("INDEXATION_TYPE")} overflow="hidden">
						<FormField errors={errors} name={`contractOptions.${index}.indexationType`}>
							<Controller
								name={`contractOptions.${index}.indexationType`}
								control={control}
								rules={{ required: true }}
								render={({ field }) => <Select
									{...field}
									fetchOptions={Object.values(IndexationType)}
									getOptionLabel={option => intl.formatMessage({ id: option })}
									disabled={field.disabled || hasTakenOptionContract}
									disableSearch
									clearable={false}
									onChange={value => {
										field.onChange(value);
										if (value === IndexationType.None) {
											setValue(`contractOptions.${index}.useEndPriceOfTheContract`, true);
											setValue(`contractOptions.${index}.pricePerUnit`,
												getPricePerUnit(previousOptionIndex, indexedPrices, agreementPricePerUnit));
										}
									}}
								/>}
							/>
						</FormField>
					</FormGroup>

					<FormGroup label={Language("CONTRACT_NOTICE_PERIOD")} overflow="hidden">
						<FormField errors={errors} name={`contractOptions.${index}.noticePeriod`}>
							<Controller
								name={`contractOptions.${index}.noticePeriod`}
								control={control}
								render={({ field }) => <NoticePeriodSelect
									{...field}
									disabled={field.disabled || hasTakenOptionContract}
								/>}
							/>
						</FormField>
					</FormGroup>
				</Grid>

				<Grid size={{ xs: 6 }}>
					{currentOption?.indexationType !== IndexationType.None &&
						<>
							<FormGroup label={Language("INDEXATION_DATE")} overflow="hidden" required>
								<FormField errors={errors} name={`contractOptions.${ index }.indexationDate`}>
									<Controller
										name={`contractOptions.${ index }.indexationDate`}
										control={control}
										rules={{
											required: true,
											validate: (value: number | null | undefined, formValues) => {
												if (!value) {
													return;
												}

												const { from, to }: TimeWindowInput = formValues.contractOptions?.[index]
													.validityWindow ?? { from: undefined, to: undefined };
												if (from && to) {
													if (value < from || value > to) {
														return intl.formatMessage({ id: "INDEXATION_DATE_NOT_WITHIN_VALIDITY_WINDOW" });
													}
												}
											},
										}}
										render={({ field }) =>
											<DatePicker
												{...field}
												value={field.value ?? undefined}
												minDate={currentOption?.validityWindow?.from ? new Date(currentOption.validityWindow.from) : undefined}
												maxDate={currentOption?.validityWindow?.to ? new Date(currentOption.validityWindow.to) : undefined}
												disabled={field.disabled || hasTakenOptionContract}
											/>}
									/>
								</FormField>
							</FormGroup>

							<FormGroup label={Language("INDEXATION_AMOUNT")} overflow="hidden" required>
								<FormField
									errors={errors}
									name={`contractOptions.${index}.indexationAmount`}
									min={0}
									max={999999999}>
									<Controller
										name={`contractOptions.${index}.indexationAmount`}
										control={control}
										rules={{ required: true }}
										render={({ field }) =>
											<Input
												{...field}
												value={field.value ?? ""}
												type="number"
												addonBefore={currentOption?.indexationType === IndexationType.FixedAmount && currency.symbol}
												addonAfter={([ IndexationType.FixedPercentage, IndexationType.CpiPercentage ] as Array<IndexationType | undefined>)
													.includes(currentOption?.indexationType ?? undefined)
													&& <PercentRounded sx={{ fontSize: 16 }} />}
												disabled={field.disabled || hasTakenOptionContract}
											/>}
									/>
								</FormField>
							</FormGroup>
						</>
					}

					<FormGroup
						label={Language(`PRICE_PER_${priceUnit ?? "UNIT"}`)}
						tooltip={priceUnit === null && intl.formatMessage({ id: "ADD_RENT_AGREEMENT_TO_SPECIFY_UNIT" })}
						overflow="hidden"
						required>
						<FormField
							errors={errors}
							name={`contractOptions.${index}.pricePerUnit`}
							min={0}
							max={999999999}>
							<Controller
								name={`contractOptions.${index}.pricePerUnit`}
								control={control}
								rules={{ required: true }}
								render={({ field }) =>
									<Input
										{ ...field }
										value={ currentOption?.pricePerUnit ?? "" }
										type="number"
										addonBefore={currency.symbol}
										disabled={ (currentOption?.useEndPriceOfTheContract ?? false)
											|| field.disabled || hasTakenOptionContract }
									/>
								}
							/>
						</FormField>
					</FormGroup>

					<div style={{ display: currentOption?.indexationType === IndexationType.None ? "none" : undefined }}>
						<IndexedPricePerUnitPreview
							indexedPrices={indexedPrices}
							setIndexedPrices={setIndexedPrices}
							currentOption={currentOption}
							index={index}
							setPricePerUnitForNextOption={(value) => {
								if (sortedOptions[orderedIndex + 1]?.useEndPriceOfTheContract) {
									const nextOrderedOptionRealIndex = contractOptions
										?.indexOf(sortedOptions[orderedIndex + 1]) ?? -1;
									if (nextOrderedOptionRealIndex > -1) {
										setValue(`contractOptions.${nextOrderedOptionRealIndex}.pricePerUnit`, value);
									}
								}
							}}
							currencySymbol={currency.symbol}
							priceUnit={priceUnit} />

						<FormGroup label={""} overflow="hidden">
							<Controller
								name={`contractOptions.${index}.useEndPriceOfTheContract`}
								control={control}
								render={({ field }) =>
									<UseEndPriceOfTheContractField
										field={field}
										index={index}
										currentOption={currentOption}
										previousOptionIndex={previousOptionIndex}
										indexedPrices={indexedPrices}
										setValue={setValue}
										hasTakenOptionContract={hasTakenOptionContract}
										agreementPricePerUnit={agreementPricePerUnit}
									/>} />
						</FormGroup>
					</div>
				</Grid>
			</Grid>
		</FormArrayRow>
	);
};

const IndexedPricePerUnitPreview = ({
	indexedPrices,
	setIndexedPrices,
	currentOption,
	index,
	setPricePerUnitForNextOption,
	currencySymbol,
	priceUnit,
}: IndexedPricePerUnitPreviewProps) => {
	const value = calculateIndexedPricePerUnit(currentOption);

	if (value === null && indexedPrices.has(index)) {
		setIndexedPrices(map => {
			map.delete(index);
			return new Map(map);
		});
		setPricePerUnitForNextOption(value);
	} else if (value !== null && value !== indexedPrices.get(index)) {
		setIndexedPrices(map => new Map(map.set(index, value)));
		setPricePerUnitForNextOption(value);
	}

	return (
		<FormGroup
			label={Language(`INDEXED_PRICE_PER_${priceUnit ?? "UNIT"}`)}
			tooltip={priceUnit === null && intl.formatMessage({ id: "ADD_RENT_AGREEMENT_TO_SPECIFY_UNIT" })}
			overflow="hidden"
			required>
			<Input
				value={value ?? ""}
				type="number"
				addonBefore={currencySymbol}
				disabled />
		</FormGroup>
	);
};

const UseEndPriceOfTheContractField = ({
	field,
	index,
	currentOption,
	previousOptionIndex,
	indexedPrices,
	setValue,
	hasTakenOptionContract,
	agreementPricePerUnit,
}: UseEndPriceOfTheContractFieldProps) => {
	const disabledCauses: string[] = [];

	const pricePerUnit = getPricePerUnit(previousOptionIndex, indexedPrices, agreementPricePerUnit);

	if (previousOptionIndex === null && currentOption?.pricePerUnit === null && pricePerUnit !== null) {
		setValue(`contractOptions.${index}.pricePerUnit`, pricePerUnit);
	}

	if (!currentOption?.validityWindow?.from) {
		disabledCauses.push(intl.formatMessage({ id: "VALIDITY_WINDOW_NOT_SPECIFIED" }));
	}

	if (pricePerUnit === null) {
		// Cannot calculate `pricePerUnit` and `previous option` either exists or the contract should be used instead
		let cause: string | null = null;
		if (previousOptionIndex !== null && previousOptionIndex !== -1) {
			// the order number of this option is known, and it is not the first one
			cause = "PREVIOUS_OPTION_HAS_NO_INDEXED_PRICE";
		} else if (previousOptionIndex === null || agreementPricePerUnit === null) {
			// it is the first options, or order number is unknown, and there is no rent agreement
			cause = "NO_RENT_AGREEMENT";
		}

		if (cause !== null) {
			disabledCauses.push(intl.formatMessage({ id: cause }));
		}
	}

	return (
		<Tooltip
			title={disabledCauses.length > 0 ? (
				<span style={{ display: "flex", flexDirection: "column", textAlign: "center" }}>
					{disabledCauses.map((warning) => <span key={warning}>{intl.formatMessage({ id: warning })}</span>)}
				</span>
			) : undefined}>
			<span>
				<Checkbox
					{...field}
					value={undefined}
					checked={field.value}
					disabled={(disabledCauses.length > 0 && !field.value) || field.disabled || hasTakenOptionContract}
					defaultChecked={currentOption?.useEndPriceOfTheContract ?? false}
					label={Language("USE_END_PRICE_OF_THE_CONTRACT")}
					onChange={(value) => {
						field.onChange(value);
						setValue(`contractOptions.${index}.pricePerUnit`, pricePerUnit);
					}} />
			</span>
		</Tooltip>
	);
};

export default ContractOptionsForm;
